import React, { useState } from 'react'
import { Download, Upload, LogOut, Trash2, AlertTriangle } from 'lucide-react'
import SelectiveExportModal from '../components/modals/SelectiveExportModal'
import ImportModal from '../components/modals/ImportModal'
import ManagersList from '../components/managers/ManagersList'
import authService from '../services/authService'
import managerService from '../services/managerService'
import { formatCurrency } from '../utils/formatters'
import './Settings.scss'

const Settings = ({
  shopInfo,
  products,
  purchases,
  loading,
  onImportSuccess,
  onLogout,
  onFormatShop,
  isRenewSubscription,
  userRole,
}) => {
  const [showExportModal, setShowExportModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)

  const currentPlan = authService.getCurrentPlan()
  const daysUntilExpiry = authService.getDaysUntilExpiry()
  const expiryDate = authService.formatExpiryDate()
  const canExport = managerService.hasPermission('exportData')
  const isOwner = userRole === 'owner'

  const handleLogout = async () => {
    const connected = await authService.isOnline()
    if (!connected) {
      alert(
        'Déconnexion impossible. Veuillez vérifier votre connexion internet.',
      )
    } else {
      const confirmed = window.confirm(
        'Êtes-vous sûr de vouloir vous déconnecter ?',
      )
      if (confirmed) {
        onLogout()
      }
    }
  }

  const handleFormatShop = () => {
    if (!isOwner) {
      alert('Action réservée au propriétaire de la boutique')
      return
    }
    onFormatShop()
  }

  const goToRenewSubscription = async () => {
    const connected = await authService.isOnline()
    if (!connected) {
      alert('Votre connexion Internet semble interrompue.')
    } else {
      isRenewSubscription({ shopId: shopInfo.id, renewSubscription: true })
    }
  }

  return (
    <div className='content-space'>
      <div className='section-header'>
        <h2 className='section-title'>Paramètres</h2>
      </div>

      {/* Alerte expiration - Propriétaire uniquement */}
      {daysUntilExpiry > 0 && daysUntilExpiry <= 7 && (
        <div className='card warning-card'>
          <div className='warning-content'>
            <AlertTriangle size={24} className='warning-icon' />
            <div>
              <h4>Votre plan expire bientôt</h4>
              <p>
                Il vous reste <strong>{daysUntilExpiry} jour(s)</strong> avant
                l'expiration. Renouvelez dès maintenant.
              </p>
              <button
                onClick={goToRenewSubscription}
                className='btn warning-btn'
              >
                Renouveler maintenant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Informations du plan */}
      {currentPlan && (
        <div className='card'>
          <h3 className='text-lg font-semibold mb-4'>Abonnement actuel</h3>
          <div className='form-group'>
            <div className='form-grid cols-2'>
              <div className='form-field'>
                <label>Plan actif</label>
                <p className='font-medium'>{currentPlan.name}</p>
              </div>
              <div className='form-field'>
                <label>Montant payé</label>
                <p className='font-medium'>
                  {formatCurrency(currentPlan.price)}
                </p>
              </div>
            </div>
            <div className='form-grid cols-2'>
              <div className='form-field'>
                <label>Sessions simultanées</label>
                <p className='font-medium'>
                  {currentPlan.maxSessions}{' '}
                  {currentPlan.maxSessions === 1 ? 'appareil' : 'appareils'}
                </p>
              </div>
              <div className='form-field'>
                <label>Expire le</label>
                <p className='font-medium'>{expiryDate || 'Non défini'}</p>
              </div>
            </div>
            {daysUntilExpiry > 7 && (
              <div className='plan-status success'>
                ✓ Votre abonnement est actif
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sauvegarde et Données */}
      <div className='card'>
        <h3 className='text-lg font-semibold mb-4'>Sauvegarde et Données</h3>
        <div className='form-group'>
          {canExport && (
            <div>
              <h4 className='font-medium mb-2'>Exporter les données</h4>
              <p className='text-sm mb-3'>
                Exportez vos données avec options de sélection.
              </p>
              <button
                onClick={() => setShowExportModal(true)}
                className='btn success'
              >
                <Download size={16} className='btn-icon' />
                Exporter les données
              </button>
            </div>
          )}

          <div>
            <h4 className='font-medium mb-2'>Importer les données</h4>
            <p className='text-sm mb-3'>
              Importez des données avec gestion automatique des conflits.
            </p>
            <button
              onClick={() => setShowImportModal(true)}
              className='btn primary'
            >
              <Upload size={16} className='btn-icon' />
              Importer les données
            </button>
          </div>
        </div>
      </div>

      {/* Informations boutique */}
      <div className='card'>
        <h3 className='text-lg font-semibold mb-4'>
          Informations de la boutique
        </h3>
        <div className='form-group'>
          <div className='form-grid cols-2'>
            <div className='form-field'>
              <label>Nom de la boutique</label>
              <p className='font-medium'>
                {shopInfo.shopName || 'Non renseigné'}
              </p>
            </div>
            <div className='form-field'>
              <label>Représentant</label>
              <p className='font-medium'>
                {shopInfo.ownerName || 'Non renseigné'}
              </p>
            </div>
          </div>
          <div className='form-grid cols-2'>
            <div className='form-field'>
              <label>Email</label>
              <p className='font-medium'>{shopInfo.email || 'Non renseigné'}</p>
            </div>
            <div className='form-field'>
              <label>Téléphone</label>
              <p className='font-medium'>{shopInfo.phone || 'Non renseigné'}</p>
            </div>
          </div>
          {!isOwner && (
            <div className='role-info'>
              <p>
                🛡️ Vous êtes connecté en tant que <strong>Gérant</strong>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Gestion des Gérants - Propriétaire uniquement */}
      {isOwner && <ManagersList />}

      {/* Statistiques */}
      {/* {isOwner && (
        <div className='card'>
          <h3 className='text-lg font-semibold mb-4'>Statistiques</h3>
          <div className='card-grid cols-3-lg'>
            <div className='card'>
              <div className='text-2xl font-bold text-primary'>
                {products.length}
              </div>
              <div className='text-sm'>Produits enregistrés</div>
            </div>
            <div className='card'>
              <div className='text-2xl font-bold text-success'>
                {purchases.length}
              </div>
              <div className='text-sm'>Achats enregistrés</div>
            </div>
            <div className='card'>
              <div className='text-2xl font-bold text-purple'>
                {formatCurrency(
                  purchases.reduce((sum, p) => sum + p.totalAmount, 0),
                )}
              </div>
              <div className='text-sm'>Chiffre d'affaires total</div>
            </div>
          </div>
        </div>
      )} */}

      {/* Zone dangereuse - Propriétaire uniquement */}
      {isOwner && (
        <div className='card danger-card'>
          <h3 className='text-lg font-semibold mb-4'>Zone dangereuse</h3>
          <div className='form-group'>
            <div>
              <h4 className='font-medium mb-2'>Formater la boutique</h4>
              <p className='text-sm mb-3'>
                Supprime définitivement tous les produits et achats. Cette
                action est irréversible.
              </p>
              <button onClick={handleFormatShop} className='btn danger'>
                <Trash2 size={16} className='btn-icon' />
                Formater la boutique
              </button>
            </div>
            <div>
              <h4 className='font-medium mb-2'>Déconnexion</h4>
              <p className='text-sm mb-3'>
                Déconnectez-vous. Vos données resteront sauvegardées sur votre
                appareil.
              </p>
              <button onClick={handleLogout} className='btn logOutBtn'>
                <LogOut size={16} className='btn-icon' />
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Déconnexion gérants (hors zone dangereuse) */}
      {!isOwner && (
        <div className='card'>
          <h3 className='text-lg font-semibold mb-4'>Déconnexion</h3>
          <div className='form-group'>
            <p className='text-sm mb-3'>
              Déconnectez-vous de votre session gérant. Vos données resteront
              sauvegardées sur votre appareil.
            </p>
            <button
              style={{ width: 'fit-content', marginTop: '-10px' }}
              onClick={handleLogout}
              className='btn logOutBtn'
            >
              <LogOut size={16} className='btn-icon' />
              Se déconnecter
            </button>
          </div>
        </div>
      )}

      {/* À propos */}
      <div className='card'>
        <h3 className='text-lg font-semibold mb-4'>À propos</h3>
        <div className='text-sm form-group'>
          <p>Application de Gestion Commerciale</p>
          <p>Version 1.0, Powered by LAGUINDA WS</p>
          <p>Données stockées localement</p>
          <p>Compatible PC, tablette et smartphone.</p>
        </div>
      </div>

      {/* Modals */}
      {canExport && (
        <SelectiveExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          products={products}
          purchases={purchases}
        />
      )}

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={onImportSuccess}
      />
    </div>
  )
}

export default Settings
